/**
 * NotchesEventsPage -- forward-looking macro + earnings calendar.
 *
 * Shows the next 14 days of high-impact events that materially affect
 * SPX/SPY/QQQ/IWM/ES trade decisions. Tier-colored, grouped by ET date,
 * with affected-symbol chips so subscribers can mentally filter by what
 * they trade.
 *
 * Backing source: GET /market/events -> market-events-prod DDB, refreshed
 * daily at 5pm ET by iv-monitor-events-ingest from FMP + earnings cache +
 * deterministic VIX expirations.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { marketEventsService, MarketEvent, ImpactTier, Country } from '../../services/marketEventsService';
import '../Dashboard.css';

const TIER_COLOR: Record<ImpactTier, { bg: string; border: string; label: string; impact: string }> = {
  tier1: { bg: 'rgba(220,38,38,0.10)',  border: '#dc2626', label: '#ef4444', impact: 'High impact' },
  tier2: { bg: 'rgba(234,179,8,0.10)',  border: '#eab308', label: '#facc15', impact: 'Moderate' },
  tier3: { bg: 'rgba(100,116,139,0.10)',border: '#64748b', label: '#94a3b8', impact: 'Watchlist' },
};

const COUNTRY_LABEL: Record<Country, string> = {
  US: 'US', EU: 'EU', UK: 'UK', JP: 'JP', CN: 'CN', CA: 'CA', OPEC: 'OPEC',
};

function fmtTimeET(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
}

function fmtDateLong(yyyymmdd: string): string {
  const d = new Date(yyyymmdd + 'T12:00:00Z');
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long', month: 'short', day: 'numeric',
  });
}

function groupByDate(events: MarketEvent[]): Map<string, MarketEvent[]> {
  const m = new Map<string, MarketEvent[]>();
  events.forEach((e) => {
    if (!m.has(e.eventDate)) m.set(e.eventDate, []);
    m.get(e.eventDate)!.push(e);
  });
  Array.from(m.values()).forEach((arr) => {
    arr.sort((a: MarketEvent, b: MarketEvent) => a.eventTime.localeCompare(b.eventTime));
  });
  return m;
}

export const NotchesEventsPage: React.FC = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState<MarketEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    marketEventsService.get(token, 14)
      .then(r => { if (!cancelled) { setEvents(r.events); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e?.message || 'Failed to load events'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [token]);

  const shell = (body: React.ReactNode) => (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px', color: '#e5e7eb' }}>
        {body}
      </div>
    </div>
  );

  if (loading) {
    return shell(<div style={{ color: '#94a3b8' }}>Loading upcoming events...</div>);
  }
  if (error) {
    return shell(<div style={{ color: '#ef4444' }}>{error}</div>);
  }
  if (!events || events.length === 0) {
    return shell(<div style={{ color: '#94a3b8' }}>No high-impact events in the next 14 days.</div>);
  }

  const grouped = groupByDate(events);
  const dates = Array.from(grouped.keys()).sort();

  return shell(
    <>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 4, margin: 0 }}>Upcoming Events</h1>
      <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
        Macro releases, central-bank decisions, and major earnings that move SPX, SPY, QQQ, IWM, and ES.
        Updated daily at 5pm ET.
      </p>

      {dates.map(date => {
        const dayEvents = grouped.get(date)!;
        return (
          <div key={date} style={{ marginBottom: '1.75rem' }}>
            <div style={{
              fontSize: '0.9rem', fontWeight: 700, color: '#f1f5f9',
              borderBottom: '1px solid #334155', paddingBottom: 4, marginBottom: 10,
              letterSpacing: 0.3,
            }}>
              {fmtDateLong(date)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dayEvents.map((e, idx) => {
                const t = TIER_COLOR[e.impactTier];
                return (
                  <div key={`${e.eventDate}-${e.eventTime}-${idx}`} style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(75px, auto) minmax(40px, auto) 1fr auto',
                    alignItems: 'center', gap: 12,
                    padding: '8px 12px', borderRadius: 8,
                    background: t.bg, border: `1px solid ${t.border}55`,
                    borderLeft: `3px solid ${t.border}`,
                  }}>
                    <span style={{ fontSize: 12, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtTimeET(e.eventTime)} ET
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
                      background: 'rgba(255,255,255,0.08)', color: '#cbd5e1', letterSpacing: 0.5,
                    }}>{COUNTRY_LABEL[e.country] || e.country}</span>
                    <span style={{ fontSize: 13, color: '#e5e7eb', fontWeight: 500 }}>
                      {e.name}
                      {(e.estimate || e.previous) && (
                        <span style={{ marginLeft: 8, color: '#64748b', fontSize: 11 }}>
                          {e.estimate ? `est ${e.estimate}${e.unit || ''}` : `prev ${e.previous}${e.unit || ''}`}
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: t.label, letterSpacing: 0.3,
                      }}>{t.impact.toUpperCase()}</span>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {e.affectedSymbols.slice(0, 5).map(s => (
                          <span key={s} style={{
                            fontSize: 9, padding: '1px 5px', borderRadius: 3,
                            background: 'rgba(255,255,255,0.05)', color: '#94a3b8',
                            fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                          }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: '2rem', fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic', borderTop: '1px solid #1e293b', paddingTop: 12 }}>
        Source: FMP economic calendar + symbol-events cache. Times shown in America/New_York.
        Events outside US/EU/UK/JP/CN/CA + the MAG7-plus-banks earnings watchlist are filtered out.
      </div>
    </>
  );
};
